import type {
  AfriexFeature as CoreAfriexFeature,
  AfriexFeatureLimit,
  AfriexFeatureMeta as CoreAfriexFeatureMeta,
  Serialized,
} from '@afriex/core/domain/types';

// AfriexFeatureFrequency is an enum (used at runtime: Object.values(...) in
// LimitInput.tsx/FeatureValidator.ts, AfriexFeatureFrequency.Off as a value),
// so it needs a value re-export, not a type-only one. AfriexFeatureStatus
// likewise (the status filter's option values in BusinessFeaturesFilters.tsx).
export {AfriexRegionCode, AfriexOS} from '@afriex/core/domain/enums';
export {
  AfriexFeatureFrequency,
  AfriexFeatureStatus,
} from '@afriex/core/domain/types';
export type {
  AfriexFeatureLimit,
  AfriexFeatureMetaFlags,
  AfriexFeatureTagMap,
  AfriexFeatureTags,
} from '@afriex/core/domain/types';

// NOTE: kept divergent from core on purpose. Adopting core's enum here would
// make "Currency" immediately selectable in the live admin feature-flag rule
// builder (FeaturePredicateInput.tsx drives its field dropdown directly off
// `Object.values(AfriexFeatureField)`), which is a real UI change to an
// internal tool, not a type-only one. The server already fully validates
// AfriexFeatureField.Currency (FeatureRequestValidator dispatches to
// validateCurrencyTag against AfriexCurrencyCode); what's actually incomplete
// is admin's OWN client-side mirror of that validation, in exactly two spots:
// the commented-out case in FeatureValidator.ts's validateTagField, and the
// commented-out case in FeatureRuleValueInput.tsx's mapFieldTypeToValue.
// Uncommenting those two (plus this enum's Currency member) is all that's
// needed to ship it; see PR discussion for the full writeup.
export enum AfriexFeatureField {
  // Currency = "CURRENCY",
  Country = 'COUNTRY',
  Region = 'REGION',
  UserId = 'USERID',
  Business = 'BUSINESS',
  Email = 'EMAIL',
  Group = 'GROUP',
  Role = 'ROLE',
  OS = 'OS',
  IP = 'IP',
  SignupDate = 'SIGNUP_DATE',
  TransactionCount = 'TRANSACTION_COUNT',
  TransactionVolume = 'TRANSACTION_VOLUME',
}

// A draft/in-progress limit, for form state that has not been submitted yet
// (e.g. useFeatureUpdate.ts seeds a brand new feature with `limit: {}`
// before the user fills in the Limit form fields). AfriexFeatureLimit itself
// mirrors core's validated, submittable shape (type and value required);
// this is the deliberately looser counterpart, following the same
// draft-vs-wire split used for checkout-web's session input types.
export type AfriexFeatureLimitDraft = Partial<AfriexFeatureLimit>;

// Extension pattern: admin reads JSON over HTTP, so `lastUsedAt` (a `Date`
// core-side) is serialized to an ISO string here rather than a plain re-export.
export type AfriexFeatureMeta = Omit<CoreAfriexFeatureMeta, 'lastUsedAt'> & {
  lastUsedAt?: string | null;
};

// Extension pattern: admin wraps the shared timestamp fields (and any other
// Date-bearing field) in core's Serialized<> helper, since admin reads JSON
// over HTTP and core's AfriexFeature.extends AfriexTimestamps uses Date.
// `meta` is overridden separately because it needs the admin-local
// AfriexFeatureMeta above (Serialized<> alone would already turn its
// `lastUsedAt` into a string, but this keeps the field typed by the named,
// reusable local alias rather than an anonymous mapped type).
export interface AfriexFeature extends Omit<
  Serialized<CoreAfriexFeature>,
  'meta'
> {
  meta?: AfriexFeatureMeta;
}
